import { useCallback } from 'react';
import { useActor } from '@xstate/react';
import { Input, Button } from '@crazyegginc/hatch';

import { useDebounce, useModal, useSite } from '/src/hooks';

import Wizard from '/src/components/wizard/Wizard';
import { TriggerCard } from './TriggerCard';
import { ConfirmLeaveWizardModal } from '/src/components/wizard/ConfirmLeaveWizardModal';
import { SupportLinks } from '/src/support';
import { SupportLink } from '/src/features/_global/components/SupportLink';

import { ReactComponent as PlusIcon } from '@crazyegginc/hatch/dist/images/icon-plus.svg';
import { ReactComponent as ArrowIcon } from '@crazyegginc/hatch/dist/images/icon-arrow.svg';

import { WizardSitePicker } from '/src/components/site-picker/index';
import { GOAL_ACTIONS, STATES } from '/src/features/goals/machines/wizard';

export function GoalTriggerWizard({ service }) {
  const [state, send] = useActor(service);
  const modal = useModal();
  const isSaving = state.matches(STATES.SAVING_GOAL);

  const showConfirmModal = useCallback(
    ({ onConfirm = null }) => {
      modal.show(<ConfirmLeaveWizardModal onConfirm={onConfirm} />);
    },
    [modal],
  );

  const isNextEnabled = useDebounce(
    () => !!state.context.payload.name && (service?.nextState?.({ type: GOAL_ACTIONS.NEXT })?.changed ?? false),
    500,
  );

  return (
    <Wizard>
      <Wizard.Header>
        <Wizard.CloseButton
          onClick={() => showConfirmModal({ onConfirm: () => send(GOAL_ACTIONS.CANCEL) })}
          defaultPath={state.context.previousPath || '/goals'}
        />
      </Wizard.Header>
      <Wizard.Content dontMask={true}>
        <div className="mx-auto max-w-[740px]">
          <Wizard.Title>What conversion do you want to track?</Wizard.Title>

          <div className="mx-auto max-w-[740px]">
            <GoalForm service={service} />
          </div>
        </div>
      </Wizard.Content>
      <Wizard.SimpleFooter
        currentStep={1}
        totalSteps={2}
        showMeta={true}
        title="Conversion setup"
        cancelText={state.context.draft ? 'Save draft & close' : 'Cancel'}
        nextEnabled={!isSaving && isNextEnabled}
        nextDisabledText={isSaving && 'Saving...'}
        onBack={() => send(GOAL_ACTIONS.BACK)}
        onNext={() => send(GOAL_ACTIONS.NEXT)}
        onCancel={() => send(GOAL_ACTIONS.CANCEL)}
        confirmCancel={!state.context.draft}
      />
    </Wizard>
  );
}

function GoalForm({ service }) {
  const [state] = useActor(service);
  const { send } = service;
  const isEditing = service.initialState.value === STATES.INITIALIZE_EDIT;

  const isAbTestFlow = !!state.context.draft;

  const onSiteSelect = useCallback(
    (site) => {
      send({ type: GOAL_ACTIONS.SELECT_SITE, site });
    },
    [send],
  );

  const { sites, selectedSite, selectSite } = useSite({
    onlySelectFromQueryParam: true,
    onSelect: onSiteSelect,
  });

  return (
    <>
      <>
        <div className="text-header-2 mb-5 mt-10">This conversion is for this site:</div>
        <WizardSitePicker
          disabled={isEditing || isAbTestFlow}
          sites={sites}
          selectedSite={selectedSite}
          selectSite={(site) => {
            selectSite(site);
          }}
          onSelected={() => {
            setTimeout(() => {
              document.querySelector('#goal-trigger-group')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 200);
          }}
        />
      </>

      <>
        <div id="goal-trigger-group">
          <h2 className="text-header-2 mb-5 mt-10">Let&#39;s set up a trigger for this Conversion...</h2>
          {state.context.payload.triggers.map((trigger, index) => (
            <TriggerCard
              service={service}
              triggersCount={state.context.payload.triggers.length}
              trigger={trigger}
              index={index}
              actions={{ insertTrigger: () => {}, removeTrigger: () => {} }}
              key={`TriggerCard:${index}`}
            />
          ))}
          <div className="pt-5">
            <SupportLink
              className="text-link font-bold items-center inline-flex"
              href={SupportLinks.siteSettings.conversionTracking}
            >
              Need more control? Set up conversions with our API <ArrowIcon className="w-[12px] fill-current ml-2" />
            </SupportLink>
            <Button
              variant="secondary"
              className="mt-10"
              onClick={() => send({ type: GOAL_ACTIONS.ADD_TRIGGER, index: state.context.payload.triggers.length - 1 })}
            >
              <PlusIcon className="mr-2.5 h-[15px] w-[15px] fill-current" />
              Add another trigger
            </Button>
          </div>
        </div>
        <div className="text-header-2 mb-5 mt-10">What should we name your Conversion goal?</div>
        <Input
          id="name"
          name="name"
          aria-label="name"
          // error={urlErrorMessage}
          className="!h-[50px]"
          onChange={(e) => send({ type: GOAL_ACTIONS.CHANGE, key: 'name', value: e.target.value })}
          value={state.context.payload.name}
          // rightIcon={checkUrlLoading ? <Spinner className="h-5 w-5 text-cadet-blue-500" /> : null}
        />
      </>
    </>
  );
}
